// src/components/NumberFormatSelector.tsx
/**
 * Phase 3: Number Format Selector Component
 * Provides UI for selecting and configuring number formats
 */

import React from 'react';
import { useDispatch } from 'react-redux';
import type { NumberFormatConfig } from '../types/redux';
import {
  setFormatType,
  setDecimalPlaces,
  setCurrencySymbol,
  setCustomUnit,
  updateNumberFormatProperty,
} from '../store/slices/secondaryLineSlice';
import {
  setFormatType as setPrimaryFormatType,
  setDecimalPlaces as setPrimaryDecimalPlaces,
  setCurrencySymbol as setPrimaryCurrencySymbol,
  setCustomUnit as setPrimaryCustomUnit,
  updateNumberFormatProperty as updatePrimaryNumberFormatProperty,
} from '../store/slices/numberLinesSlice';
import { CURRENCY_SYMBOLS, COMMON_UNITS } from '../lib/numberFormatting';

export interface NumberFormatSelectorProps {
  /** Current format configuration */
  config: NumberFormatConfig;
  /** Whether this is for primary or secondary line */
  line: 'primary' | 'secondary';
  /** Whether the selector is disabled */
  disabled?: boolean;
}

export const NumberFormatSelector: React.FC<NumberFormatSelectorProps> = ({
  config,
  line,
  disabled = false,
}) => {
  const dispatch = useDispatch();

  // Use primary or secondary actions based on line prop
  const formatActions = line === 'primary' ? {
    setFormatType: setPrimaryFormatType,
    setDecimalPlaces: setPrimaryDecimalPlaces,
    setCurrencySymbol: setPrimaryCurrencySymbol,
    setCustomUnit: setPrimaryCustomUnit,
    updateNumberFormatProperty: updatePrimaryNumberFormatProperty,
  } : {
    setFormatType,
    setDecimalPlaces,
    setCurrencySymbol,
    setCustomUnit,
    updateNumberFormatProperty,
  };

  const handleTypeChange = (type: NumberFormatConfig['type']) => {
    dispatch(formatActions.setFormatType(type));
  };

  const handleDecimalPlacesChange = (places: number | 'auto') => {
    dispatch(formatActions.setDecimalPlaces(places as 0 | 1 | 2 | 3 | 'auto'));
  };

  const handleCurrencySymbolChange = (symbol: string) => {
    dispatch(formatActions.setCurrencySymbol(symbol));
  };

  const handleCurrencyPositionChange = (position: 'before' | 'after') => {
    dispatch(formatActions.updateNumberFormatProperty({ 
      key: 'currencyPosition', 
      value: position 
    }));
  };

  const handleCustomUnitChange = (unit: string) => {
    dispatch(formatActions.setCustomUnit(unit));
  };

  const handleUnitPositionChange = (position: 'before' | 'after') => {
    dispatch(formatActions.updateNumberFormatProperty({ 
      key: 'unitPosition', 
      value: position 
    }));
  };

  return (
    <div className="space-y-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Format Type
        </label>
        <select
          value={config.type}
          onChange={(e) => handleTypeChange(e.target.value as NumberFormatConfig['type'])}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 
                     rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                     disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="integer">Integer</option>
          <option value="decimal">Decimal</option>
          <option value="fraction">Fraction</option>
          <option value="percentage">Percentage</option>
          <option value="currency">Currency</option>
          <option value="custom">Custom Unit</option>
        </select>
      </div>

      {/* Decimal Places Selector (for decimal, percentage, custom) */}
      {/* Note: Currency always uses 2 decimal places, so it's excluded here */}
      {(config.type === 'decimal' || config.type === 'percentage' || 
        config.type === 'custom') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Decimal Places
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => handleDecimalPlacesChange('auto')}
              disabled={disabled}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                ${config.decimalPlaces === 'auto'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Auto
            </button>
            {[0, 1, 2, 3].map((places) => (
              <button
                key={places}
                onClick={() => handleDecimalPlacesChange(places)}
                disabled={disabled}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${config.decimalPlaces === places
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {places}dp
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Fraction Format Selector */}
      {config.type === 'fraction' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Fraction Format
          </label>
          <div className="flex gap-2">
            <button
              onClick={() => dispatch(formatActions.updateNumberFormatProperty({ 
                key: 'fractionFormat', 
                value: 'mixed' 
              }))}
              disabled={disabled}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                ${config.fractionFormat === 'mixed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Mixed
            </button>
            <button
              onClick={() => dispatch(formatActions.updateNumberFormatProperty({ 
                key: 'fractionFormat', 
                value: 'improper' 
              }))}
              disabled={disabled}
              className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                ${config.fractionFormat === 'improper'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              Improper
            </button>
          </div>
        </div>
      )}

      {/* Currency Symbol Selector */}
      {config.type === 'currency' && (
        <>
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Currency values always display with 2 decimal places
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Currency Symbol
            </label>
            <select
              value={config.currencySymbol}
              onChange={(e) => handleCurrencySymbolChange(e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 
                         rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {CURRENCY_SYMBOLS.map((curr) => (
                <option key={curr.code} value={curr.symbol}>
                  {curr.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Symbol Position
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleCurrencyPositionChange('before')}
                disabled={disabled}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${config.currencyPosition === 'before'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Before (£10)
              </button>
              <button
                onClick={() => handleCurrencyPositionChange('after')}
                disabled={disabled}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${config.currencyPosition === 'after'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                After (10£)
              </button>
            </div>
          </div>
        </>
      )}

      {/* Custom Unit Selector */}
      {config.type === 'custom' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Unit
            </label>
            <div className="flex gap-2">
              <select
                value={config.customUnit === 'custom' ? 'custom' : (config.customUnit || '')}
                onChange={(e) => {
                  if (e.target.value === 'custom') {
                    handleCustomUnitChange('custom');
                  } else {
                    handleCustomUnitChange(e.target.value);
                  }
                }}
                disabled={disabled}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 
                           rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">-- Select Unit --</option>
                {COMMON_UNITS.map((unit) => (
                  <option key={unit.unit} value={unit.unit}>
                    {unit.label}
                  </option>
                ))}
                <option value="custom">Custom...</option>
              </select>
              
              {(config.customUnit === 'custom' || (!COMMON_UNITS.find(u => u.unit === config.customUnit) && config.customUnit)) && (
                <input
                  type="text"
                  placeholder="Enter unit"
                  value={config.customUnit === 'custom' ? '' : (config.customUnit || '')}
                  onChange={(e) => handleCustomUnitChange(e.target.value)}
                  disabled={disabled}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 
                             rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                             disabled:opacity-50 disabled:cursor-not-allowed"
                />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Unit Position
            </label>
            <div className="flex gap-2">
              <button
                onClick={() => handleUnitPositionChange('before')}
                disabled={disabled}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${config.unitPosition === 'before'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Before (g100)
              </button>
              <button
                onClick={() => handleUnitPositionChange('after')}
                disabled={disabled}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors
                  ${config.unitPosition === 'after'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                After (100g)
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NumberFormatSelector;

